import type { BasketResponse, BasketAddRequest, BasketAddResponse } from '~/types/on24-klient'
import { useQuery, keepPreviousData, useMutation, useQueryClient } from '@tanstack/vue-query'
import { usePageQuery } from './shared'
import { useBasketState } from '~/composables/states'

const query = () => {
  const app = useNuxtApp()

  return useQuery({
    queryKey: ['basket'],
    queryFn: async () => {
      return await app.$api<BasketResponse>('/order/basket')
    },
    placeholderData: keepPreviousData,
  })
}

export const useBasketQuery = usePageQuery(query)

export const useRelativeQuantityChangeMutation = () => {
  const queryClient = useQueryClient()
  const app = useNuxtApp()

  return useMutation({
    mutationFn: ({ productId, amount, optionIds, variationId }: { productId: number; amount: number; variationId: number | null; optionIds: number[] | null }) =>
      app.$api<BasketAddResponse>('/order/add', {
        method: 'POST',
        body: {
          productId: productId,
          quantity: amount,
          mode: 'RELATIVE',
          variationId: variationId ?? 0,
          optionIds: optionIds,
        } satisfies BasketAddRequest,
      }),
    onSuccess(response) {
      useBasketState().value = response.itemsInBasket
    },
    onSettled() {
      queryClient.invalidateQueries({ queryKey: ['basket'] })
    },
  })
}

export const useAbsoluteQuantityChangeMutation = () => {
  const queryClient = useQueryClient()
  const app = useNuxtApp()

  return useMutation({
    mutationFn: ({ itemId, absKogus, variationId, optionIds }: { itemId: number; absKogus: number; optionIds: number[] | null; variationId: number | null }) =>
      app.$api<BasketAddResponse>('/order/add', {
        method: 'POST',
        body: {
          productId: itemId,
          quantity: absKogus,
          mode: 'ABSOLUTE',
          variationId: variationId ?? 0,
          optionIds: optionIds,
        } satisfies BasketAddRequest,
      }),
    onSuccess(response) {
      useBasketState().value = response.itemsInBasket
    },
    onSettled() {
      queryClient.invalidateQueries({ queryKey: ['basket'] })
    },
  })
}
